import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

const MasjidForm = () => {
  const [masjidData, setMasjidData] = useState({
    name: '',
    pointOfContact: '',
    contactNumber: '',
    district: '',
    image: '',
    mapUrl: ''
  });

  const [profilePreview, setProfilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMasjidData({ ...masjidData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMasjidData({ ...masjidData, image: reader.result });
        setProfilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadStatus('');

    try {
      await axios.post(`${process.env.REACT_APP_REQUESTING_API}/api/masjids/add`, masjidData);
      setUploadStatus('✅ Masjid added successfully!');
      setMasjidData({
        name: '',
        pointOfContact: '',
        contactNumber: '',
        district: '',
        image: '',
        mapUrl: ''
      });
      setProfilePreview(null);
      setTimeout(() => setUploadStatus(''), 5000);
    } catch (error) {
      console.error(error);
      setUploadStatus('❌ Error adding masjid: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-black text-gray-800 mb-3">
            Add a Masjid
          </h1>
          <p className="text-gray-600 text-lg">
            Register a new masjid in your district
          </p>
        </motion.div>

        {/* Upload Status Message */}
        {uploadStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl font-semibold text-center ${
              uploadStatus.includes('✅')
                ? 'bg-green-100 text-green-800 border-2 border-green-300'
                : 'bg-red-100 text-red-800 border-2 border-red-300'
            }`}
          >
            {uploadStatus}
          </motion.div>
        )}

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200"
          >
            <div className="space-y-6">
              {/* Masjid Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Masjid Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={masjidData.name}
                  onChange={handleChange}
                  placeholder="e.g., Masjid Al-Rahman"
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                />
              </div>

              {/* Two Column Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Point of Contact */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Point of Contact *
                  </label>
                  <input
                    type="text"
                    name="pointOfContact"
                    value={masjidData.pointOfContact}
                    onChange={handleChange}
                    placeholder="Enter contact person name"
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  />
                </div>

                {/* Contact Number */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Contact Number *
                  </label>
                  <input
                    type="text"
                    name="contactNumber"
                    value={masjidData.contactNumber}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* District */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  District *
                </label>
                <select
                  name="district"
                  value={masjidData.district}
                  onChange={handleChange}
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                >
                  <option value="">-- Select District --</option>
                  {[
                    "Srikakulam", "Vizianagram", "Visakhapatnam", "Kakinada",
                    "East Godavari", "Konaseema", "West Godavari", "Eluru", "Vijayawada",
                    "Krishna", "Guntur", "Palnadu", "Bapatla", "Prakasam", "Nellore",
                    "Nandayala", "Kurnool", "Anantapur", "SriSathyasai", "Kadapa",
                    "Annamayya", "TIRUPATI", "CHITTOOR"
                  ].map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Upload Masjid Image *
                </label>
                <input
                  type="file"
                  id="profileImage"
                  onChange={handleFileChange}
                  accept="image/*"
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-800 file:text-white file:font-semibold hover:file:bg-gray-900 cursor-pointer"
                />
                {profilePreview && (
                  <div className="mt-4 flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <img
                      src={profilePreview}
                      alt="Profile Preview"
                      className="w-32 h-32 object-cover rounded-lg shadow-md border-2 border-gray-200"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-700">✓ Image loaded successfully</p>
                      <p className="text-xs text-gray-500 mt-1">Preview of masjid image</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Map URL */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Google Maps URL *
                </label>
                <input
                  type="text"
                  name="mapUrl"
                  value={masjidData.mapUrl}
                  onChange={handleChange}
                  placeholder="Paste Google Maps link here"
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                />
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Open Google Maps, right-click on the location, and copy the URL
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-800 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Adding Masjid...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Masjid
                  </span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default MasjidForm;



// working 16/12/25

// import React, { useState } from 'react';
// import axios from 'axios';

// const MasjidForm = () => {
//   const [masjidData, setMasjidData] = useState({
//     name: '',
//     pointOfContact: '',
//     contactNumber: '',
//     district: '',
//     image: '',
//     mapUrl: ''
//   });

//   const [profilePreview, setProfilePreview] = useState(null);

//   // Handle text input changes
//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setMasjidData({ ...masjidData, [name]: value });
//   };

//   // Handle image file change
//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setMasjidData({ ...masjidData, image: reader.result });
//         setProfilePreview(reader.result);
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//   // Handle form submission
//   const handleSubmit = async (e) => {
//   e.preventDefault();
//   try {
//     await axios.post(`${process.env.REACT_APP_REQUESTING_API}/api/masjids/add`, masjidData);
//     alert('Masjid added successfully');
//     // Reset form
//     setMasjidData({
//       name: '',
//       pointOfContact: '',
//       contactNumber: '',
//       district: '',
//       image: '',
//       mapUrl: ''
//     });
//     setProfilePreview(null);
//   } catch (error) {
//     console.error(error);
//     alert('Error adding masjid: ' + (error.response?.data?.message || error.message));
//   }
// };


//   return (
//     <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
//       <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">Add a Masjid</h2>
//       <form onSubmit={handleSubmit} className="space-y-4">
//         {/* Masjid Name */}
//         <input
//           type="text"
//           name="name"
//           value={masjidData.name}
//           onChange={handleChange}
//           placeholder="Masjid Name"
//           required
//           className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//         />

//         {/* Point of Contact */}
//         <input
//           type="text"
//           name="pointOfContact"
//           value={masjidData.pointOfContact}
//           onChange={handleChange}
//           placeholder="Point of Contact"
//           required
//           className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//         />

//         {/* Contact Number */}
//         <input
//           type="text"
//           name="contactNumber"
//           value={masjidData.contactNumber}
//           onChange={handleChange}
//           placeholder="Contact Number"
//           required
//           className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//         />

//         {/* District */}
//         <select
//           name="district"
//           value={masjidData.district}
//           onChange={handleChange}
//           required
//           className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//         >
//           <option value="">Select District</option>
//           {/* List of districts */}
//           {[
//             "Srikakulam", "Vizianagram", "Visakhapatnam", "Kakinada",
//             "East Godavari", "Konaseema", "West Godavari", "Eluru", "Vijayawada",
//             "Krishna", "Guntur", "Palnadu", "Bapatla", "Prakasam", "Nellore",
//             "Nandayala", "Kurnool", "Anantapur", "SriSathyasai", "Kadapa",
//             "Annamayya", "TIRUPATI", "CHITTOOR"
//           ].map(district => (
//             <option key={district} value={district}>{district}</option>
//           ))}
//         </select>

//         {/* Image Upload */}
//         <div>
//           <label htmlFor="profileImage" className="block font-semibold mb-1">Upload Profile Image</label>
//           <input
//             type="file"
//             id="profileImage"
//             onChange={handleFileChange}
//             accept="image/*"
//             required
//             className="block w-full"
//           />
//           {profilePreview && (
//             <img
//               src={profilePreview}
//               alt="Profile Preview"
//               className="w-24 h-24 mt-2 object-cover border rounded"
//             />
//           )}
//         </div>

//         {/* Map URL */}
//         <input
//           type="text"
//           name="mapUrl"
//           value={masjidData.mapUrl}
//           onChange={handleChange}
//           placeholder="Google Maps URL"
//           required
//           className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//         />

//         {/* Submit Button */}
//         <div className="flex justify-center">
//           <button
//             type="submit"
//             className="px-8 py-2 bg-gray-600 text-white font-semibold rounded-md shadow-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
//           >
//             Submit
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default MasjidForm;
